import { Box } from '@mui/material';
import { type ReactNode } from 'react';
import { useSetRecoilState } from 'recoil';
import { useToolRailSlotRef } from '~/components/toolbar/shared/ToolRailSlots';
import { toolOptionsDockState } from '~/state/atoms/toolsAtoms';

/**
 * Lays out `children`, the views, under the layer that the tool options dock
 * and the draggable panels render into. The layer stays mounted while the
 * step changes, so an open operation keeps its values when the step's tools
 * change. It lets every press through; only the panels on it take them.
 *
 * A press on a view collapses a docked dock to one line, so it covers as
 * little of the image as it can while the user works there.
 */
export default function ToolOptionsDockLayer({
    children,
}: {
    children: ReactNode;
}) {
    const panelsRef = useToolRailSlotRef('panels');
    const setDock = useSetRecoilState(toolOptionsDockState);

    return (
        <Box
            position="relative"
            flex={1}
            minWidth={0}
            height="100%"
            // Capture phase: controls in the views stop the press from
            // bubbling. Never prevented, so the view still gets it.
            onPointerDownCapture={(event) => {
                if (
                    event.target instanceof Element &&
                    event.target.closest('[data-viewport-container]')
                ) {
                    setDock((dock) =>
                        dock.mode === 'open' && dock.detachedPosition === null
                            ? { ...dock, mode: 'thin' }
                            : dock
                    );
                }
            }}
        >
            {/* First, so Tab reaches a tool's options from the rail before
                the views; its z-index keeps it over them. */}
            <Box
                ref={panelsRef}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    // Over the app header, so a panel dragged off the rail
                    // shows anywhere in the window. Under the file drop area
                    // while files are dragged in.
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                }}
            />
            {children}
        </Box>
    );
}
